import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
    Warehouse,
    Truck,
    HardHat,
    ListChecks,
    Warning,
    ArrowRight,
    Calendar
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const StatCard = ({ icon: Icon, label, value, color, link }) => (
    <Link
        to={link}
        className="stat-card group"
        data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
        <div className="flex items-start justify-between">
            <div>
                <p className="label-overline mb-2">{label}</p>
                <p className="font-heading font-bold text-3xl">{value}</p>
            </div>
            <div
                className={`w-12 h-12 rounded-sm flex items-center justify-center ${color}`}
            >
                <Icon size={24} weight="fill" />
            </div>
        </div>
        <div className="mt-4 flex items-center text-sm text-[#0055FF] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
            Ver detalles <ArrowRight size={16} className="ml-1" />
        </div>
    </Link>
);

const AlertItem = ({ tool }) => (
    <div className="flex items-center justify-between py-3 border-b border-[#E5E5E5] last:border-0">
        <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-sm flex items-center justify-center">
                <Warning size={18} className="text-red-600" weight="fill" />
            </div>
            <div>
                <p className="font-medium text-sm">{tool.name}</p>
                <p className="text-xs text-[#555555]">{tool.category}</p>
            </div>
        </div>
        <div className="text-right">
            <p className="font-bold text-red-600">{tool.quantity}</p>
            <p className="text-xs text-[#555555]">Min: {tool.min_stock}</p>
        </div>
    </div>
);

const RecentLogItem = ({ log }) => (
    <div className="flex items-center gap-4 py-3 border-b border-[#E5E5E5] last:border-0">
        <div className="w-10 h-10 bg-[#FFD700]/20 rounded-sm flex items-center justify-center">
            <Calendar size={20} className="text-[#111111]" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="font-medium text-sm truncate">{log.activities || "Sin actividades"}</p>
            <p className="text-xs text-[#555555]">{new Date(log.date).toLocaleDateString("es-ES")}</p>
        </div>
        <div className="text-right">
            <p className="text-xs text-[#555555]">{log.workers_count} trabajadores</p>
        </div>
    </div>
);

const DashboardPage = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API}/dashboard/stats`);
            setStats(response.data);
        } catch (error) {
            toast.error("Error al cargar estadísticas");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in" data-testid="dashboard-page">
            <div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl">Dashboard</h1>
                <p className="text-[#555555] mt-1">Resumen general de tu empresa</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    icon={Warehouse}
                    label="Herramientas"
                    value={stats?.tools_count || 0}
                    color="bg-[#FFD700] text-[#111111]"
                    link="/inventario"
                />
                <StatCard
                    icon={Truck}
                    label="Vehículos"
                    value={`${stats?.available_vehicles || 0}/${stats?.vehicles_count || 0}`}
                    color="bg-[#0055FF] text-white"
                    link="/vehiculos"
                />
                <StatCard
                    icon={HardHat}
                    label="Obras Activas"
                    value={stats?.active_projects || 0}
                    color="bg-green-500 text-white"
                    link="/obras"
                />
                <StatCard
                    icon={ListChecks}
                    label="Tareas Pendientes"
                    value={stats?.pending_tasks || 0}
                    color="bg-orange-500 text-white"
                    link="/tareas"
                />
            </div>

            {/* Bottom Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Low Stock Alerts */}
                <div className="bg-white border border-[#E5E5E5] rounded-sm">
                    <div className="p-4 border-b border-[#E5E5E5] flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Warning size={20} className="text-red-500" weight="fill" />
                            <h2 className="font-heading font-bold">Alertas de Stock Bajo</h2>
                        </div>
                        <span className="badge-danger">{stats?.low_stock_tools?.length || 0}</span>
                    </div>
                    <div className="p-4">
                        {stats?.low_stock_tools?.length > 0 ? (
                            stats.low_stock_tools.slice(0, 5).map((tool) => (
                                <AlertItem key={tool.id} tool={tool} />
                            ))
                        ) : (
                            <p className="text-center text-[#555555] py-8">
                                No hay alertas de stock bajo
                            </p>
                        )}
                        {stats?.low_stock_tools?.length > 5 && (
                            <Link
                                to="/inventario"
                                className="block text-center text-[#0055FF] font-medium mt-4 hover:underline"
                            >
                                Ver todas ({stats.low_stock_tools.length})
                            </Link>
                        )}
                    </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white border border-[#E5E5E5] rounded-sm">
                    <div className="p-4 border-b border-[#E5E5E5] flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Calendar size={20} className="text-[#0055FF]" weight="fill" />
                            <h2 className="font-heading font-bold">Actividad Reciente</h2>
                        </div>
                    </div>
                    <div className="p-4">
                        {stats?.recent_logs?.length > 0 ? (
                            stats.recent_logs.map((log) => (
                                <RecentLogItem key={log.id} log={log} />
                            ))
                        ) : (
                            <p className="text-center text-[#555555] py-8">
                                No hay registros recientes
                            </p>
                        )}
                        <Link
                            to="/diario"
                            className="block text-center text-[#0055FF] font-medium mt-4 hover:underline"
                        >
                            Ver diario de obra
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
